import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Briefcase, CalendarClock, FileText, Users } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/hr/recruitment/')({
  component: RecruitmentDashboard,
})

function RecruitmentDashboard() {
  const { data: openingsData, isLoading: openingsLoading } = useQuery(
    orpc.hr.listJobOpenings.queryOptions({ input: { page: 1, limit: 100 } }),
  )

  const { data: applicantsData, isLoading: applicantsLoading } = useQuery(
    orpc.hr.listApplicants.queryOptions({ input: { page: 1, limit: 100 } }),
  )

  const { data: interviewsData, isLoading: interviewsLoading } = useQuery(
    orpc.hr.listInterviews.queryOptions({ input: { page: 1, limit: 100 } }),
  )

  const openings = (openingsData?.data ?? []) as {
    id: number
    status: string
    title: string
    positionsCount: number | null
  }[]
  const applicants = (applicantsData?.data ?? []) as { id: number; status: string }[]
  const interviews = (interviewsData?.data ?? []) as {
    id: number
    scheduledAt: string | Date
    status: string
  }[]

  const openPositions = openings.filter((o) => o.status === 'open').length
  const pipelineCount = applicants.filter((a) =>
    ['applied', 'screening', 'interview', 'offer'].includes(a.status),
  ).length

  const now = new Date()
  const weekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000)
  const interviewsThisWeek = interviews.filter((i) => {
    const scheduled = new Date(i.scheduledAt)
    return (
      i.status === 'scheduled' &&
      scheduled >= now &&
      scheduled <= weekFromNow
    )
  }).length

  const pendingOffers = applicants.filter((a) => a.status === 'offer').length

  const isLoading = openingsLoading || applicantsLoading || interviewsLoading

  const stats = [
    {
      title: 'Open Positions',
      value: openPositions,
      description: 'Active job openings',
      icon: Briefcase,
    },
    {
      title: 'Pipeline',
      value: pipelineCount,
      description: 'Applicants in progress',
      icon: Users,
    },
    {
      title: 'Interviews This Week',
      value: interviewsThisWeek,
      description: 'Scheduled interviews',
      icon: CalendarClock,
    },
    {
      title: 'Pending Offers',
      value: pendingOffers,
      description: 'Awaiting response',
      icon: FileText,
    },
  ]

  return (
    <div className="container mx-auto py-6 px-4">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Recruitment</h1>
        <p className="text-muted-foreground">Manage job openings, applicants, and interviews</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {stats.map((stat) => (
          <Card key={stat.title}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">{stat.title}</CardTitle>
              <stat.icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <Skeleton className="h-8 w-16" />
              ) : (
                <div className="text-2xl font-bold">{stat.value}</div>
              )}
              <p className="text-xs text-muted-foreground mt-1">{stat.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Link to="/hr/recruitment/openings">
        <Card
          className="hover:shadow-md transition-shadow cursor-pointer"
        >
          <CardHeader className="flex flex-row items-center gap-4 pb-2">
            <div className="p-2 rounded-lg bg-muted">
              <Briefcase className="h-5 w-5" />
            </div>
            <CardTitle className="text-lg">Job Openings</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Manage job openings, post positions, and track applicant pipelines
            </p>
          </CardContent>
        </Card>
        </Link>
      </div>
    </div>
  )
}
